/**
 * P3-PROPOSAL — reworked A4 spec-sheet PDF (post CEO review 2026-10-15).
 *
 * "The right image is not good. The ski shape is totally wrong. I want the
 *  font and layout of the left version." — Pete
 *
 * Rework scope (all three):
 * 1. SHAPE: the first PDF's plan view was deformed — the arc sampler's sweep
 *    semantics were inverted after the y-flip, so sidecut arcs bowed THROUGH
 *    the ski (two bulges + gap). Rebuilt: arcs now sampled in SVG (y-down)
 *    space with the spec's own sweep convention, then mapped in one affine
 *    step — no flag reinterpretation to get wrong.
 * 2. FONT: Barlow (the Wagner brand font, from wagnerskis.com's own CDN),
 *    embedded as CIDFontType2 subsets (400/500/700) — real text layer.
 * 3. LAYOUT: measured from the reference PDF itself (extract-ref-layout):
 *    rail x31.3..92.7/y32..810, header y≈40-90, DIMENSIONS heading y≈170,
 *    plan view y195.7..228.3 at 0.225pt/mm, PROFILE curve y329.8..350.2,
 *    CONSTRUCTION y405-640, GRAPHICS y640-680, footer y≈700+.
 *
 * 2026-10-22 five-step rework (Proposal Kanban) — every section re-measured
 * against Pete's five attached reference crops (read via OpenRouter
 * gemini-2.5-flash) + pixel forensics on SpecSheet230702-page1.png:
 * S1 title 'YOUR CUSTOM SKI DESIGN' ALL-CAPS bold ~19pt + vector Wagner
 *     Custom logo top-right (mountain mark + WAGNER/CUSTOM wordmarks) +
 *     rule + 'HANDCRAFTED FOR:' caps label.
 * S2 tip LEFT (nose maps to low page-x), plain integer width values ABOVE
 *     the ski, arrows spanning the ski's width edge-to-edge, RADIUS label
 *     above with dotted leader, LENGTH label ABOVE its dotted dim line.
 * S3 'TIP:'/'TAIL:' caps labels + rocker/camber brackets below the baseline
 *     with vertical end-caps, dotted leaders, centered ROCKER/CAMBER/ROCKER.
 * S4 CONSTRUCTION:/FLEX PATTERN:/STIFFNESS: bold caps labels, per-layer
 *     laminate labels left of the illustration.
 * S5 'CUSTOM:' bold caps inline label + rule below the description.
 */
import type { SpecSheetData } from '../specSheet';
import type { RenderArc } from '../renderFacet';
import { pointOnArc } from '../renderFacet';
import type { PdfOp } from './types';
import { buildPdfWithFonts } from './pdfCore';
import { BARLOW_COND_W as BARLOW_COND_TABLES } from './barlowCondW';
import { BARLOW_WIDTHS } from './barlowWidths';

export const SPEC_SHEET_PDF_VERSION = '0.4';

// Wagner brand palette (2026 Brand Standards, p12 web/digital standards).
const RAIL: [number, number, number] = [0, 86 / 255, 125 / 255];       // #00567D primary
const SKI_FILL: [number, number, number] = [61 / 255, 156 / 255, 193 / 255]; // #3D9CC1 (reference sheet ski fill)
const HEADINGS: [number, number, number] = [69 / 255, 69 / 255, 69 / 255];    // #454545 headings
const GRAY: [number, number, number] = [102 / 255, 101 / 255, 101 / 255];    // #666565 body text
const INK: [number, number, number] = [34 / 255, 34 / 255, 34 / 255];        // #222222 email body
const TBD_AMBER: [number, number, number] = [0.7, 0.49, 0.0];

// Reference layout (pt, top-down) — measured from SpecSheet230702.pdf page
// render (1240x1755 @150dpi => /2.084 px->pt), five-step rework 2026-10-22.
const RAIL_X0 = 31.3, RAIL_X1 = 92.7, RAIL_Y0 = 32, RAIL_Y1 = 810;
const CONTENT_X0 = 120, CONTENT_X1 = 555;
// S1: title baseline pt 91.7 top / 67.7 bottom of caps; rule y105.1;
// HANDCRAFTED line y118-125; serial (only when present) y132-139.
const BARLOW_COND_WIDTHS = BARLOW_COND_TABLES.bold;
const HDR_RULE_Y = 105;
// S2: DIMENSIONS head y152.6-163 (Barlow bold ~15.4pt); plan band below;
// value labels y221.7-227.9 & 233.2-239.4 (two rows above the ski);
// RADIUS y233-240; LENGTH line y301.8-308.5 with label above it.
const DIM_HEAD_Y = 160;
const PLAN_Y0 = 211.7, PLAN_Y1 = 244.3;         // 32.5pt band at 0.225pt/mm (shifted +16pt 2026-11-03: the DIMENSIONS label grid moved under the head)
const LENGTH_Y = 254.3; // 10pt below the plan band (Pete 2026-10-22: closer to the ski)
// S3: PROFILE head y261-272 (~15.1pt); TIP desc y288-295; TAIL y294-309;
// sideview band ~y343-350 + upturns; strip y375-384; brackets+labels y427+.
const PROF_HEAD_Y = 278;   // +10pt 2026-11-03: clear the LENGTH line after the label grid moved under the DIMENSIONS head
const PROF_TIP_Y = 292;
const PROF_TAIL_Y = 304;
// Pete 2026-10-28: CAMBER: row under TAIL: (11pt pitch).
const PROF_CAMBER_Y = PROF_TAIL_Y + 11;
// Pete 2026-10-27: raise the sideview band so the ski sits tight under the
  // tail description — less white space between text and drawing.
  const PROF_CURVE_Y0 = 295, PROF_CURVE_Y1 = 338;
// Pete 2026-10-27: pull the bracket row up tight under the ski sideview
  // (baseline 338) — no white space between ski bottom and the dimension lines.
  const ROCKER_BARS_Y = 352;   // bracket lines; labels 8pt below
// S4 MATERIALS (Pete 2026-10-25 kanban: CONSTRUCTION->MATERIALS head,
// CORE->CONSTRUCTION sub-head). Reference geometry (SpecSheet230702 page
// forensics 2026-10-25): head y403-414; sub-blocks y429+; layer labels
// right-aligned ending x392.6 (7 text lines, ~21.1pt pitch, two-line
// CARBON WRAP / WOOD CORE); art x413.8..552.0 y412.3..567.3.
const CON_HEAD_Y = 430;
const LAM_LABEL_X = 392.6;     // layer labels are RIGHT-aligned to here
const LAM_X0 = 413.8, LAM_X1 = 552.0;   // art box (native aspect 734:839)
const LAM_Y0 = 430, LAM_Y1 = 585;
// S5: DESIGN SUMMARY (Pete 2026-10-28) — head band below YOUR BUILD; the
// Sales Recommendation paragraph (bottom of the Ski Proposal) wraps across
// the full content width. Head baseline y614: the TOPSHEET GRAPHIC block's
// wrapped 2nd line reaches ~y594 — 600 collided (caught by vision QC +
// text-layer bbox overlap 2026-10-28); 614 clears it with room for 7 body
// lines. Pete 2026-10-28 follow-ups: MORE breathing room between the
// TOPSHEET GRAPHIC block (wrap bottom ~y594) and the DESIGN SUMMARY head —
// head 614->624->636 (second spacing pass + footer deleted). FOOTER LINE
// REMOVED entirely (Pete 2026-10-28, attachment): 'Wagner Custom Skis -
// handcrafted in Telluride, Colorado - wagnerskis.com - Design version 1'
// no longer renders on the sheet; footerLine stays in the data contract.
const DS_HEAD_Y = 636;

// NOTE: A4_W/A4_H live in pdfCore (single source); only A4_H is needed here.
const A4_H = 841.89;

function f(n: number): string { return n.toFixed(2); }

/**
 * The rail IS the ski — Pete 2026-10-15: "Make the blue vertical bar on the
 * left side into a ski shape... the same ski shape as the horizontal ski
 * shape that now looks correct. Add the Wagner custom logo into this
 * vertical ski shape in the same area as shown on the sample document."
 *
 * The featured ski's plan-view outline (the same arcs the DIMENSIONS section
 * renders — one geometry source), rotated vertical (nose up), FILLED in the
 * brand teal, its length set by the page height (rail band y32..810) and its
 * width derived from the ski's real aspect ratio. The logo box sits on the
 * ski body near the top (the reference's measured position), on the shovel
 * where the ski is wide enough to carry it.
 */
function railSkiFromArcs(arcs: RenderArc[]): PdfOp[] {
  const pts = samplePlanViewPolyline(arcs);
  if (pts.length < 10) return [];
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const lenMm = maxX - minX;
  // Length set by the page: the reference rail band y32..810 (778pt).
  const topY = RAIL_Y0;
  const bottomY = RAIL_Y1;
  const s = (bottomY - topY) / lenMm;
  const railCx = (RAIL_X0 + RAIL_X1) / 2;
  const midY = (minY + maxY) / 2;
  const d: string[] = [];
  // Ski long axis (mm x) -> rail vertical, nose (low x) at top; width axis
  // (mm y) -> horizontal, centered on the rail.
  const map = (x: number, y: number): [number, number] =>
    [railCx + (y - midY) * s, A4_H - (topY + (x - minX) * s)];
  const [x0p, y0p] = map(pts[0][0], pts[0][1]);
  d.push(`${f(x0p)} ${f(y0p)} m`);
  for (let i = 1; i < pts.length; i++) {
    const [px, py] = map(pts[i][0], pts[i][1]);
    d.push(`${f(px)} ${f(py)} l`);
  }
  d.push('h');
  // Filled brand-teal ski body with a hairline navy edge (the reference's
  // edge treatment). THE RAIL IS THE SKI — no separate bar behind it.
  // Pete 2026-10-22 (section dial-in): "Get rid of the 'W' in the left
  // column ski" — the white W mark is removed. The rail ski stays clean
  // (the 10-15 graphic-preview future feature note still stands).
  return [{ op: 'path', d, fill: RAIL, stroke: [0, 22 / 255, 32 / 255], lineWidth: 0.7 }];
}

interface T { x: number; y: number; size: number; text: string; weight?: 400 | 500 | 700; color?: [number, number, number]; align?: 'left' | 'center' | 'right'; rotate90?: boolean; font?: 'condensed'; hScale?: number }

function textOp(t: T): PdfOp {
  let x = t.x;
  const hs = t.hScale ?? 1;
  const w = (t.font === 'condensed'
    ? textWidthCondensed(t.text, t.size)
    : textWidthBarlow(t.text, t.size, t.weight ?? 400)) * hs;
  if (t.align === 'center') x -= w / 2;
  if (t.align === 'right') x -= w;
  return { op: 'text', x, y: A4_H - t.y, size: t.size, text: t.text, weight: t.weight ?? 400, color: t.color ?? INK, align: 'left', rotate90: t.rotate90, font: t.font, hScale: t.hScale };
}

/** Width using the Barlow Condensed Bold advance widths. Table values are
 *  em/1000 (e.g. 613 = 0.613em) — divide by 1000 before scaling by size. */
function textWidthCondensed(s: string, size: number): number {
  let w = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    w += (BARLOW_COND_WIDTHS[String(c)] ?? 450) / 1000;
  }
  return w * size;
}

function textWidthBarlow(s: string, size: number, weight: number): number {
  const table = BARLOW_WIDTHS[weight === 700 ? 'bold' : weight === 500 ? 'medium' : 'regular'];
  let w = 0;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    w += (table[String(c)] ?? 0.55) * 1000;
  }
  return (w / 1000) * size;
}

function num(n: SpecSheetData['dimensions']['lengthCm'], unit = '', digits = 1): string {
  if (n.kind === 'sourced') return `${n.value.toFixed(digits)}${unit}`;
  return 'TBD';
}

// ---------------------------------------------------------------------------
// S2: CAD dimension arrows (reference crop style): vertical double-headed
// arrows spanning the ski's width edge-to-edge (heads ON the edges, not
// through), plain integer values ABOVE the ski, dotted shafts.
// ---------------------------------------------------------------------------

/** Vertical double-headed CAD arrow at x spanning y0..y1, value above. */
function cadVArrow(ops: PdfOp[], x: number, y0: number, y1: number, value: string): void {
  const yTop = Math.min(y0, y1), yBot = Math.max(y0, y1);
  // dotted shaft
  ops.push({ op: 'path', d: [
    `${f(x)} ${f(A4_H - yTop)} m`, `${f(x)} ${f(A4_H - yBot)} l`, 'S',
  ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.8, dash: [0.1, 2.6] });
  // heads: small solid V at each edge pointing inward
  for (const [ya, dir] of [[yTop, 1], [yBot, -1]] as const) {
    ops.push({ op: 'path', d: [
      `${f(x - 2)} ${f(A4_H - (ya + dir * 4.5))} m`, `${f(x)} ${f(A4_H - ya)} l`, `${f(x + 2)} ${f(A4_H - (ya + dir * 4.5))} l`, 'S',
    ], stroke: GRAY, lineWidth: 0.6 });
  }
  // value centered above the arrow (ref: plain '149 mm' row)
  ops.push(textOp({ x, y: yTop - 6, size: 8.5, text: value, color: GRAY, align: 'center' }));
}

/** Horizontal dotted CAD dimension line with end ticks, value ABOVE the line. */
function cadHDim(ops: PdfOp[], x0: number, x1: number, y: number, label: string, value: string): void {
  // Normalize: the caller may pass the ends in either order (the S2 call
  // passes tail-first) — every segment below needs x0 < x1.
  const xa = Math.min(x0, x1), xb = Math.max(x0, x1);
  // End ticks + outward arrowheads (Pete 2026-10-22: arrows ON the length
  // line — CAD style, heads pointing outward at each end).
  ops.push({ op: 'path', d: [
    `${f(xa)} ${f(A4_H - (y + 4))} m`, `${f(xa)} ${f(A4_H - (y - 4))} l`, 'S',
    `${f(xb)} ${f(A4_H - (y + 4))} m`, `${f(xb)} ${f(A4_H - (y - 4))} l`, 'S',
  ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.8 });
  // Arrowheads matching the waist-width arrows' style (thin V strokes,
  // ±2pt wings / 4.5pt depth). Pete 2026-10-22: REVERSED from the previous
  // pass — heads INSIDE the span (other side of the extension lines),
  // apex on the tick pointing OUTWARD.
  for (const [xe, dir] of [[xa, -1], [xb, 1]] as const) {
    ops.push({ op: 'path', d: [
      `${f(xe - dir * 4.5)} ${f(A4_H - (y - 2))} m`, `${f(xe)} ${f(A4_H - y)} l`, `${f(xe - dir * 4.5)} ${f(A4_H - (y + 2))} l`, 'S',
    ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.6 });
  }
  // Inline label (S2 ref: the dotted line BREAKS around 'LENGTH: 183 cm' and
  // the text forms part of the dimension line, vertically centered on it).
  const text = `${label}: ${value}`;
  const textSize = 9;
  const gapHalf = textWidthBarlow(text, textSize, 500) / 2 + 6;
  const cx = (xa + xb) / 2;
  const gL = Math.max(xa, cx - gapHalf);
  const gR = Math.min(xb, cx + gapHalf);
  if (gL - xa > 4) {
    ops.push({ op: 'path', d: [
      `${f(xa)} ${f(A4_H - y)} m`, `${f(gL)} ${f(A4_H - y)} l`, 'S',
    ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.8, dash: [0.1, 2.6] });
  }
  if (xb - gR > 4) {
    ops.push({ op: 'path', d: [
      `${f(gR)} ${f(A4_H - y)} m`, `${f(xb)} ${f(A4_H - y)} l`, 'S',
    ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.8, dash: [0.1, 2.6] });
  }
  // baseline = y + ~1/3 of cap height so the text centers on the line;
  // color matches the tip/waist/tail dimension values (Pete 2026-10-22).
  ops.push(textOp({ x: cx, y: y + 2.6, size: textSize, text, color: GRAY, weight: 500, align: 'center' }));
}

/** Dashed leader from a label down to the ski edge + radius value. */
/** Radius callout, S2-reference style: the label sits in the row above the
 *  ski, and a short DOTTED VERTICAL leader drops straight down with a small
 *  arrowhead touching the ski's top edge. */
function cadRadiusLeader(ops: PdfOp[], fromX: number, fromY: number, toX: number, toY: number, text: string): void {
  // dotted vertical drop from just under the label to the ski edge
  ops.push({ op: 'path', d: [
    `${f(toX)} ${f(A4_H - fromY)} m`, `${f(toX)} ${f(A4_H - toY)} l`, 'S',
  ], stroke: [68 / 255, 68 / 255, 68 / 255], lineWidth: 0.8, dash: [0.1, 2.6] });
  // small arrowhead pointing DOWN at the ski edge
  ops.push({ op: 'path', d: [
    `${f(toX - 2)} ${f(A4_H - (toY - 4.5))} m`, `${f(toX)} ${f(A4_H - toY)} l`, `${f(toX + 2)} ${f(A4_H - (toY - 4.5))} l`, 'S',
  ], stroke: GRAY, lineWidth: 0.6 });
  // label CENTERED above fromX (its own anchor), just above the drop's top
  // end; the drop itself sits under the label's number (Pete 2026-10-22).
  // label/value color matches the tip/waist/tail dimension values (Pete
  // 2026-10-22: same color/darkness).
  ops.push(textOp({ x: fromX, y: fromY - 2, size: 9, text, color: GRAY, weight: 500, align: 'center' }));
}

/** Wrap text to a max width (pt) using the Barlow width table. ASCII-safe:
 * typographic chars the subset doesn't carry are replaced upstream. */
function wrapText(s: string, size: number, weight: 400 | 500 | 700, maxWidth: number): string[] {
  const words = s.split(' ');
  const lines: string[] = [];
  let cur = '';
  for (const w of words) {
    const t = cur ? `${cur} ${w}` : w;
    if (textWidthBarlow(t, size, weight) <= maxWidth || !cur) cur = t;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  return lines;
}

// Reference JPEG assets (see pdf/referenceAssets.ts): laminate stack
// placeholder illustration (Pete 2026-10-22 step 4) + the thin mountain
// strip under the profile curve (reference image 11, y362-387).
//
// TODO (Pete 2026-10-27, P3-PROPOSAL follow-up): REPLACE the laminate-stack
// JPEG below — it is the REFERENCE PDF's stock illustration, NOT this ski's
// actual build. Pete: "I really like this image, but it's just a placeholder.
// We want to give a similar infographic that shows the ACTUAL BUILD for each
// ski." Build a data-driven exploded-layup infographic from the order form
// (coreMaterial / structuralLayer / baseMaterial / aramid / topsheet +
// materialsStack order) so every sheet shows its own construction. Until
// then the placeholder keeps the reference layout; revisit after Step 4.
import { LAMINATE_STACK_JPEG_B64, WAGNER_LOGO_JPEG_B64 } from './referenceAssets';

/** Endpoint proximity test for chaining profile sub-paths (SVG space). */
function near(a: [number, number], b: [number, number], eps = 1e-6): boolean {
  return Math.abs(a[0] - b[0]) < eps && Math.abs(a[1] - b[1]) < eps;
}

export function renderSpecSheetPdf(data: SpecSheetData, arcs: RenderArc[]): Uint8Array {
  const ops: PdfOp[] = [];

  // ---- rail: THE SKI IS THE BAR (unchanged directive) ----
  ops.push(...railSkiFromArcs(arcs));

  // ---- STEP 1 (S1): header — ALL-CAPS title, logo top-right, rule, name ----
  // S1 header — rebuilt to the CEO mockup (img_2304dbfc65f5, 2026-10-22):
  // dominant condensed-bold title, logo top-right extending ABOVE the title
  // cap, full-width rule, bold 'HANDCRAFTED FOR:' label + regular name.
  // Title: Barlow Condensed Bold 30pt (cap 21pt), cap top y72 / bottom y93.
  ops.push(textOp({ x: CONTENT_X0, y: 93, size: 30, text: 'YOUR CUSTOM SKI DESIGN', weight: 700, color: [33 / 255, 33 / 255, 33 / 255], font: 'condensed', hScale: 0.84 }));
  // Wagner Custom logo — cropped UNALTERED from the reference sheet (brand
  // standards: logo unaltered). Bottom sits 6.8pt above the rule, height 60pt
  // (mark extends above the title cap, per the mockup).
  // Wagner Custom logo — cropped UNALTERED from the reference sheet (brand
  // standards: logo unaltered). Pete 2026-10-22 (section dial-in): "The top
  // of logo in the upper right is being chopped off" — at 60pt tall the
  // mark overhung into the top margin (top y38), which email previews and
  // print margins shave. Re-fitted INSIDE the header band: aspect-true
  // 42pt height (220x154 source), bottom 6.8pt above the rule → top y56.3,
  // clear of the page margin AND the title cap (y72) — no overhang.
  // Asset re-cropped 2026-10-22: (975,30)-(1170,208) — the old crop clipped
  // the mountain peak (ink at asset top edge). True logo bounds y39..200,
  // x980..1156 on the ref page; 195x178px = aspect 1.096.
  const LOGO_H = 60;
  const LOGO_W = LOGO_H * (195 / 178);
  // bottom edge 6.8pt above the rule → logo spans y56.2..98.2 top-down
  ops.push({ op: 'image', x: CONTENT_X1 - LOGO_W, y: A4_H - (HDR_RULE_Y - 6.8), w: LOGO_W, h: LOGO_H, jpegBase64: WAGNER_LOGO_JPEG_B64 });
  // Rule starts where the title starts (Pete 2026-10-22: "it should start
  // where the Your starts") and runs to the content's right edge.
  ops.push({ op: 'path', d: [
    `${f(CONTENT_X0)} ${f(A4_H - HDR_RULE_Y)} m`, `${f(CONTENT_X1)} ${f(A4_H - HDR_RULE_Y)} l`, 'S',
  ], stroke: [119 / 255, 119 / 255, 119 / 255], lineWidth: 1.1 });
  // Name line per the mockup: bold 'HANDCRAFTED FOR:' + regular name, indented
  // ~10.5pt from the title's left edge, sitting ~10pt below the rule.
  const NAME_LABEL = 'HANDCRAFTED FOR: ';
  const nameY = HDR_RULE_Y + 16.4;
  const nameSize = 8.5;
  const nameIndent = 13.8;
  ops.push(textOp({ x: CONTENT_X0 + nameIndent, y: nameY, size: nameSize, text: NAME_LABEL, weight: 700, color: [33 / 255, 33 / 255, 33 / 255], font: 'condensed' }));
  ops.push(textOp({
    x: CONTENT_X0 + nameIndent + textWidthCondensed(NAME_LABEL, nameSize),
    y: nameY, size: nameSize, text: data.customer.name, weight: 400, color: [97 / 255, 97 / 255, 97 / 255],
  }));
  if (data.customer.serial.kind === 'sourced') {
    ops.push(textOp({ x: CONTENT_X0 + nameIndent, y: nameY + 12, size: nameSize, text: `SERIAL NUMBER: ${Math.round(data.customer.serial.value)}`, color: INK }));
  }

  // ---- STEP 2 (S2): DIMENSIONS — plan view (tip LEFT) + CAD callouts ----
  ops.push(textOp({ x: CONTENT_X0, y: DIM_HEAD_Y, size: 15, text: 'DIMENSIONS', weight: 700, color: HEADINGS }));

  const d = data.dimensions;

  // DIMENSIONS LABEL GRID (Pete 2026-11-03, PM: delete the SPECS: line -
  // "list LENGTH, TIP, WAIST, TAIL, RADIUS with the actual dimensions below
  // those labels", the same 5-column layout as the HTML proposal: gray
  // ALL-CAPS labels on top, bold values below. Values come from the same
  // data.dimensions the plan-view arrows render - never hand-copied.)
  {
    const cells: Array<[string, string]> = [
      ['LENGTH', `${num(d.lengthCm, ' cm', 0)}`],
      ['TIP', `${num(d.tipWidthMm, ' mm', 0)}`],
      ['WAIST', `${num(d.waistWidthMm, ' mm', 0)}`],
      ['TAIL', `${num(d.tailWidthMm, ' mm', 0)}`],
      ['RADIUS', `${num(d.radiusM, ' m')}`],
    ];
    const colW = (CONTENT_X1 - CONTENT_X0) / 5;
    const gridLabelY = 174;   // directly below the DIMENSIONS head (160), above the plan band (211.7)
    const gridValueY = gridLabelY + 13;
    cells.forEach(([lab, val], i) => {
      const x = CONTENT_X0 + i * colW;
      ops.push(textOp({ x, y: gridLabelY, size: 8.5, text: lab, color: GRAY }));
      ops.push(textOp({ x, y: gridValueY, size: 12, text: val, weight: 700, color: INK }));
    });
  }

  const poly = samplePlanViewPolyline(arcs);
  const planX0 = CONTENT_X0 + 20, planW = (CONTENT_X1 - CONTENT_X0) - 40;
  // NO flip for the plan view: arcs1 x=0 is the NOSE (verified: the mapped
  // width shoulders measured 30.2 right vs 27.1 left = 1.114 ≈ 133.3/148.7
  // inverted — with flip the nose landed RIGHT; the reference wants tip LEFT,
  // and without the flip the nose lands LEFT directly).
  const mapped = mapPolyline(poly, planX0, PLAN_Y0, planW, PLAN_Y1 - PLAN_Y0, false);
  ops.push(...polylineOps(mapped, { fill: SKI_FILL, stroke: INK, lineWidth: 0.6 }));

  // Real bounds of the mapped plan view (never guessed).
  let mMinX = Infinity, mMaxX = -Infinity, mMinY = Infinity, mMaxY = -Infinity;
  for (const [px, py] of mapped) {
    if (px < mMinX) mMinX = px; if (px > mMaxX) mMaxX = px;
    if (py < mMinY) mMinY = py; if (py > mMaxY) mMaxY = py;
  }
  // S2 reference: TIP (nose, wide end, 149mm) on the LEFT. In sampled facet
  // space, low x = tail, high x = nose. The affine map preserves x order, so
  // the nose sits at the RIGHT unless we flip. Flip the x-axis so the nose
  // lands LEFT, matching the reference exactly.
  const noseSideX = mMinX;      // after flip: nose at low page x (LEFT)
  const tailSideX = mMaxX;
  const xTip = noseSideX, xTail = tailSideX;
  // Arrows at the DRAWN outline's real extremes (Pete 2026-10-22):
  //   tip arrow  -> widest point of the tip zone (left 30% of the outline)
  //   waist arrow-> narrowest point of the ski (middle band)
  //   tail arrow -> widest point of the tail zone (right 30%)
  // Width profile in PAGE space from the mapped outline.
  // Pete 2026-10-29 (Nizolek email review): the WAIST arrow read ~50mm aft of
  // the ski's narrowest point because 8pt bins (~39mm) let the argmin land a
  // full bin off the true station. 2pt bins (~10mm) place every arrow within
  // ~1pt of the measured argmax/argmin; the shaft still spans the bin's own
  // lo/hi local edges.
  const binW = 2;
  const nBins = Math.max(2, Math.ceil((mMaxX - mMinX) / binW));
  const widthProfile: Array<{ x: number; w: number; lo: number; hi: number }> = [];
  for (let i = 0; i < nBins; i++) {
    const x0 = mMinX + (mMaxX - mMinX) * i / nBins;
    const x1 = mMinX + (mMaxX - mMinX) * (i + 1) / nBins;
    let lo = Infinity, hi = -Infinity;
    for (const [px, py] of mapped) {
      if (px >= x0 && px < x1) { if (py < lo) lo = py; if (py > hi) hi = py; }
    }
    if (lo < Infinity) widthProfile.push({ x: (x0 + x1) / 2, w: hi - lo, lo, hi });
  }
  const argmaxW = (arr: typeof widthProfile): typeof widthProfile[number] | undefined => arr.length ? arr.reduce((a, b) => (b.w > a.w ? b : a)) : undefined;
  const argminW = (arr: typeof widthProfile): typeof widthProfile[number] | undefined => arr.length ? arr.reduce((a, b) => (b.w < a.w ? b : a)) : undefined;
  // Pete 2026-10-22: the arrows must run INSIDE the ski — each width arrow
  // spans the ski's LOCAL edges at that x (the profile bin's lo/hi), never the
  // global bounds (at the waist/tail the ski is narrower; global bounds made
  // the shafts overshoot the shape).
  // Pete 2026-10-29 (email review, 'waist dimension is in the wrong spot'):
  // on a shallow sidecut (R28.3m) the waist zone is FLAT to within 0.1mm for
  // ~130mm either side of the true station, so a single-bin argmin lands on
  // an arbitrary tie break. Select the CENTER of the extreme PLATEAU instead —
  // the arrow sits exactly on the measured widest/narrowest station, not on a
  // quantization tie. Local edge span (lo/hi) still comes from the nearest bin.
  const plateauCenter = (arr: typeof widthProfile, mode: 'max' | 'min'):
      { x: number; lo: number; hi: number } | undefined => {
    if (!arr.length) return undefined;
    const best = mode === 'max' ? argmaxW(arr) : argminW(arr);
    if (!best) return undefined;
    const flat = arr.filter(p => Math.abs(p.w - best.w) <= 0.02);
    const cx = (flat[0].x + flat[flat.length - 1].x) / 2;
    let near = flat[0], dd = Infinity;
    for (const p of flat) { const q = Math.abs(p.x - cx); if (q < dd) { dd = q; near = p; } }
    return { x: cx, lo: near.lo, hi: near.hi };
  };
  const tipBin = plateauCenter(widthProfile.filter(p => p.x <= mMinX + (mMaxX - mMinX) * 0.3), 'max');
  const waistBin = plateauCenter(widthProfile.filter(p => p.x > mMinX + (mMaxX - mMinX) * 0.3 && p.x < mMinX + (mMaxX - mMinX) * 0.75), 'min');
  const tailBin = plateauCenter(widthProfile.filter(p => p.x >= mMinX + (mMaxX - mMinX) * 0.7), 'max');
  const xWaist = waistBin ? waistBin.x : (mMinX + mMaxX) / 2;

  // Vertical CAD arrows INSIDE the ski: local edges per position (ref S2).
  if (tipBin) cadVArrow(ops, tipBin.x, tipBin.lo, tipBin.hi, num(d.tipWidthMm, ' mm', 0));
  if (waistBin) cadVArrow(ops, waistBin.x, waistBin.lo, waistBin.hi, num(d.waistWidthMm, ' mm', 0));
  if (tailBin) cadVArrow(ops, tailBin.x, tailBin.lo, tailBin.hi, num(d.tailWidthMm, ' mm', 0));

  // Radius: dashed leader from the label ABOVE the ski (S2 ref: the RADIUS
  // label sits above the ski, right-of-center, with a dotted leader down to
  // the edge near the waist-tail transition).
  // RADIUS sits BETWEEN the tip and waist dimension arrows (Pete 2026-10-22),
  // and the drop line centers on the NUMBER '22.6' within the label
  // (Pete 2026-10-22: 'centered below the 22.6').
  const tipArrowX = tipBin ? tipBin.x : (mMinX + mMaxX) / 2;
  const rlabX0 = (tipArrowX + xWaist) / 2;
  const radiusText = `RADIUS: ${num(d.radiusM, ' m')}`;
  // x of the number's center within the string: measure the prefix
  // 'RADIUS: ' and the number's own width.
  const rPrefixW = textWidthBarlow('RADIUS: ', 9, 500);
  // number only ('22.6', unit ' m' excluded — Pete: centered below the 22.6)
  const rNumW = textWidthBarlow(num(d.radiusM, ''), 9, 500);
  const rlabX = rlabX0 + rPrefixW + rNumW / 2 - textWidthBarlow(radiusText, 9, 500) / 2;
  // 2026-11-03 (Pete label grid): leader shortened 18 -> 8pt above the ski
  // so the callout clears the grid's value row (bottom ~187) below it.
  cadRadiusLeader(ops, rlabX0, mMinY - 8, rlabX, mMinY + 1, radiusText);

  // Length: dotted CAD dimension line below the plan view, label ABOVE it.
  cadHDim(ops, xTail, xTip, LENGTH_Y, 'LENGTH', num(d.lengthCm, ' cm', 0));

  // ---- STEP 3 (S3): PROFILE — descriptions + sideview + bracket bars ----
  const p = data.profile;
  ops.push(textOp({ x: CONTENT_X0, y: PROF_HEAD_Y, size: 15, text: 'PROFILE', weight: 700, color: HEADINGS }));

  // Tip / tail descriptions: 'TIP:'/'TAIL:' bold caps labels + FULL sentences
  // from the record (S3 ref: 'Tapered Off-Piste tip design with 30-10 tip
  // rocker for easy turn initiation and good flotation.') — never truncated
  // shorthand.
  if (p.tipDescription) {
    ops.push(textOp({ x: CONTENT_X0, y: PROF_TIP_Y, size: 9, text: 'TIP: ', weight: 700, color: [33 / 255, 33 / 255, 33 / 255] }));
    const tipW = textWidthBarlow('TIP: ', 9, 700);
    const tipSentence = `${p.tipDescription} tip design with ${p.tipRockerLabel.replace('Rocker ', '')} tip rocker for easy turn initiation and good flotation.`;
    const tipLines = wrapText(tipSentence, 9, 400, (CONTENT_X1 - CONTENT_X0) - tipW - 4);
    ops.push(textOp({ x: CONTENT_X0 + tipW + 2, y: PROF_TIP_Y, size: 9, text: tipLines[0] ?? '', color: GRAY }));
    for (let i = 1; i < tipLines.length; i++) {
      ops.push(textOp({ x: CONTENT_X0, y: PROF_TIP_Y + i * 11, size: 9, text: tipLines[i], color: GRAY }));
    }
  }
  if (p.tailDescription) {
    ops.push(textOp({ x: CONTENT_X0, y: PROF_TAIL_Y, size: 9, text: 'TAIL: ', weight: 700, color: [33 / 255, 33 / 255, 33 / 255] }));
    const tailW = textWidthBarlow('TAIL: ', 9, 700);
    const tailSentence = `${p.tailDescription} tail design with ${p.tailRockerLabel.replace('Rocker ', '')} tail rocker for soft snow performance.`;
    const tx2 = CONTENT_X0 + tailW + 2;
    const lines2 = wrapText(tailSentence, 9, 400, (CONTENT_X1 - CONTENT_X0) - tailW - 4);
    ops.push(textOp({ x: tx2, y: PROF_TAIL_Y, size: 9, text: lines2[0] ?? '', color: GRAY }));
    for (let i = 1; i < lines2.length; i++) {
      ops.push(textOp({ x: CONTENT_X0, y: PROF_TAIL_Y + i * 11, size: 9, text: lines2[i], color: GRAY }));
    }
  }
  // Pete 2026-10-28: CAMBER: line under TAIL: — customer-facing copy per
  // camber enum (profile.ts camberDescriptionFor). The wrap is computed HERE
  // so the sideview shift below can account for multi-line descriptions.
  const camW = textWidthBarlow('CAMBER: ', 9, 700);
  const camLines = wrapText(p.camberDescription, 9, 400, (CONTENT_X1 - CONTENT_X0) - camW - 4);
  ops.push(textOp({ x: CONTENT_X0, y: PROF_CAMBER_Y, size: 9, text: 'CAMBER: ', weight: 700, color: [33 / 255, 33 / 255, 33 / 255] }));
  ops.push(textOp({ x: CONTENT_X0 + camW + 2, y: PROF_CAMBER_Y, size: 9, text: camLines[0] ?? '', color: GRAY }));
  for (let i = 1; i < camLines.length; i++) {
    ops.push(textOp({ x: CONTENT_X0, y: PROF_CAMBER_Y + i * 11, size: 9, text: camLines[i], color: GRAY }));
  }

  // Sideview: profile curve (rocker/camber) + core-thickness body outline.
  // S3 reference: TIP on the LEFT. The profile SVG has tail at x=0, nose at
  // x=L — so we flip x when mapping (nose lands LEFT like the plan view).
  // Paths are sampled SEPARATELY: the longest straight path is the BASELINE
  // (dotted, under the ski); the bow + tip/tail arcs are the ski CURVE.
  const profPaths = sampleProfilePaths(p.profileSvg);
  // Pete 2026-10-28: the CAMBER: description row occupies 11pt between the
  // TAIL: line and the sideview — the whole profile drawing + bracket row
  // shift DOWN by exactly that amount so nothing collides (checked live:
  // fixed constants put the ski's ink at 321.7pt vs a 317.9pt text row).
  const PROF_DESC_SHIFT = 11 + (camLines.length - 1) * 11;
  const profBandY0 = PROF_CURVE_Y0 + PROF_DESC_SHIFT, profBandH = PROF_CURVE_Y1 - PROF_CURVE_Y0;
  const profBoxW = (CONTENT_X1 - CONTENT_X0) - 40;
  // ONE shared affine for ALL profile paths — per-path mapping normalizes each
  // sub-path to the full box (bow stretched 395pt wide, arcs distorted). The
  // shared transform uses the UNION bounds of every path.
  const profAll = profPaths.flat();
  // TRUE SCALE (Pete 2026-10-23: 'the scale is way off'): the sideview's
  // vertical pt/mm must equal its horizontal pt/mm (profBoxW / 1830mm), so a
  // 61mm tip rise draws at ~13pt, not stretched to fill the 45pt band.
  const profYScaleMm = profBoxW / 1830;
  // Anchor the BASELINE (svg y = H, the ground line) at the band bottom —
  // NOT the union maxY: for Reverse the bow dips below it, and pinning the
  // dip apex floats the whole ski (measured 1.08pt float, probe 2026-10-24).
  const mappedProfPaths = mapPolylineShared(
    profPaths.map(pp => pp.slice()),
    profAll, CONTENT_X0 + 20, profBandY0, profBoxW, profBandH, true, profYScaleMm,
    sideProfileSvgHeight(p.profileSvg));
  // classify: a path whose every point has identical y = baseline; else curve.
  const isFlat = (pp: Array<[number, number]>): boolean => {
    if (pp.length < 2) return true;
    const y0 = pp[0][1];
    return pp.every(([ , y]) => Math.abs(y - y0) < 0.15);
  };
  // The baseline is the 2-point M..L path. The camber bow at ZERO camber is
  // geometrically straight but samples to 17 collinear points — classify by
  // POINT COUNT so it keeps chaining as the curve (else the None-camber
  // chain breaks at the arc junction, probe 2026-10-24).
  const baselinePaths = mappedProfPaths.filter(pp => isFlat(pp) && pp.length <= 2);
  const curvePaths = mappedProfPaths.filter(pp => !(isFlat(pp) && pp.length <= 2));
  // baseline (S3 ref): SOLID under the camber section (between the contact
  // points — the ski's running surface), DOTTED under the rocker zones
  // (past the contacts out past tip and tail).
  const bl = baselinePaths[0];
  if (bl && bl.length >= 2) {
    const bx0 = bl[0][0], bx1 = bl[bl.length - 1][0];
    const yBl = bl[0][1];
    // Pete 2026-10-27: "lower the ski so you can see the camber limits just
    // touch the dotted line (and are the low points) (y=0). The goal is that
    // you can see the camber section and you can see the early rise extending
    // towards the tips and tails from this point." The y=0 plane (the limits,
    // the low points) sits at the baseline level (yBl); the line moves from
    // 1.5pt below to JUST TOUCHING: center-to-center gap = ski bottom
    // half-stroke (0.25) + line half-stroke (0.35) = 0.6pt — outer edges
    // meet with no visible float, and the camber arch + early rise read
    // directly off the line.
    const yDots = yBl + 0.6;   // top-down page coords: + = down
    ops.push({ op: 'path', d: [
      `${f(bx0)} ${f(A4_H - yDots)} m`, `${f(bx1)} ${f(A4_H - yDots)} l`, 'S',
    ], stroke: [170 / 255, 170 / 255, 170 / 255], lineWidth: 0.7, dash: [2, 2.5] });
  }
  // ski curve: the tail arc, camber bow, and nose arc share endpoints and
  // must be chained into ONE continuous line — naive concatenation order
  // (bow, nose, tail) draws a straight JUMP line through the ski. Chain
  // greedily by shared endpoints (within 1e-6).
  // Chain the curve sub-paths into ONE continuous line. Start from a FREE
  // END (an endpoint shared by no other path — the nose or tail TIP), then
  // repeatedly append whichever remaining path shares the chain's tail
  // point (reversing when needed). This yields tail-arc + bow + nose-arc as
  // a single polyline — no straight jump lines through the ski.
  const endpoints = new Map<string, number>();
  const key = (q: [number, number]): string => `${q[0].toFixed(6)}|${q[1].toFixed(6)}`;
  for (const cp of curvePaths) {
    if (cp.length < 2) continue;
    for (const e of [cp[0], cp[cp.length - 1]]) {
      endpoints.set(key(e), (endpoints.get(key(e)) ?? 0) + 1);
    }
  }
  // Start from the path holding a FREE endpoint, oriented so the free end is
  // the chain's HEAD and the shared end continues into the next path.
  let startIdx = -1, startReversed = false;
  for (let i = 0; i < curvePaths.length && startIdx === -1; i++) {
    const cp = curvePaths[i];
    if (cp.length < 2) continue;
    if ((endpoints.get(key(cp[0])) ?? 0) === 1) { startIdx = i; startReversed = false; }
    else if ((endpoints.get(key(cp[cp.length - 1])) ?? 0) === 1) { startIdx = i; startReversed = true; }
  }
  if (startIdx === -1) { startIdx = 0; startReversed = false; }
  const remaining = curvePaths.map(p => p.slice());
  const firstPath = remaining.splice(startIdx, 1)[0] ?? [];
  const chained: Array<[number, number]> = startReversed ? firstPath.slice().reverse() : firstPath.slice();
  while (remaining.length) {
    const tailPt = chained[chained.length - 1];
    const idx = remaining.findIndex(p => p.length >= 2 && (near(p[0], tailPt) || near(p[p.length - 1], tailPt)));
    if (idx === -1) {
      const orphan = remaining.shift();
      if (orphan && orphan.length) chained.push(...orphan);
      continue;
    }
    const nxt = remaining.splice(idx, 1)[0];
    const seg = near(nxt[0], tailPt) ? nxt : nxt.slice().reverse();
    chained.push(...seg.slice(1));
  }
  const profPts = chained;
  if (profPts.length > 1) {
    const mappedProf = profPts;
    // Core thickness sideview: same x-mapping, thickness = core profile in mm.
    // Taper is SINE per the record (coreTipTaper / coreTailTaper = "1 sine"):
    // one smooth sine ease from nose thickness -> waist across the front half,
    // waist -> tail across the rear half. No stepped guesses.
    const ct = p.coreThickness;
    if (ct.noseMm.kind === 'sourced' && ct.waistMm.kind === 'sourced' && ct.tailMm.kind === 'sourced') {
      const tN = ct.noseMm.value, tW = ct.waistMm.value, tT = ct.tailMm.value;
      // pt-per-mm VERTICALLY for the profile: TRUE SCALE — the same pt/mm as
      // the sideview's curve (profYScaleMm = profBoxW/1830). The old code used
      // the stretched band scale (0.618 pt/mm), drawing the core 2.9x too
      // thick relative to the true-scale curve (Pete 2026-10-23).
      const pxPt = profYScaleMm;
      const ex = 1;    // TRUE SCALE (Pete 2026-10-22): no thickness exaggeration —
                       // the envelope hugs the curve; taper reads via the numbers
      /** Core thickness (mm) at a fractional position along the length axis. */
      const tmm = (frac: number): number => {
        if (frac <= 0.5) {
          const u = frac * 2;                       // 0..1 nose -> waist
          return tN + (tW - tN) * Math.sin((u * Math.PI) / 2);
        }
        const u = (frac - 0.5) * 2;                 // 0..1 waist -> tail
        return tW + (tT - tW) * Math.sin((u * Math.PI) / 2);
      };
      const nProf = mappedProf.length;
      const half = (i: number): number => (tmm(i / (nProf - 1)) * pxPt * ex) / 2;
      // Visible camber arch — PER-CAMBER-TYPE display (boosts finalized by
      // Pete 2026-10-25: 'I like Low 1.8 / Medium 2.5 / High 2.5'):
      //   Low -> 1.8, Medium -> 2.5, High -> 2.5, None -> flat, Reverse
      //   -> center of the camber ON the line + 4mm at the widest points.
      // "Boost" multiplies the curve's OWN camber bow: the mapped SVG bow
      // already carries the true camberHeightMm arch (quadratic with the
      // apex pinned to camberHeightMm), so the extra synthetic lift is
      // (boost-1) x bow — the old flat 1.8 lift ON TOP of the bow
      // double-counted (measured 2.82pt drawn vs 1.55pt intended). TRUE
      // peak heights are the record's (profileCurve.ts CAMBER_HEIGHTS_MM):
      // Low 3, Medium 5, High 8mm — Pete's chat shorthand "4mm" was an
      // approximation. At boost 1 Low draws its true 3mm (0.65pt): subtle
      // BY INSTRUCTION.
      const camberEnum = p.camberEnum ?? '1';
      const camberMm = p.camberHeightMm.kind === 'sourced' ? p.camberHeightMm.value : 0;
      const isReverse = camberEnum === '-1';
      const isFlatNone = camberEnum === '0';
      const boost = camberEnum === '3' ? 2.5 : camberEnum === '2' ? 2.5 : camberEnum === '1' ? 1.8 : camberEnum === '-1' ? 2.5 : 1;
      // Contacts in facet mm (tail x=0 -> nose x=1830): tail 149.59, nose 1619.96.
      const spanL = 149.59, spanR = 1830 - 210.04, spanW = spanR - spanL;
      // The mapped SVG bow in mm at facet x — quadratic through the apex:
      // y(t) = y0 + 2t(1-t)(yC-y0) with yC-y0 = -2·camber => bow = 2·camber·t(1-t).
      const bowT = (fx: number): number =>
        Math.min(1, Math.max(0, (fx - spanL) / spanW));
      const bowSvgMm = (fx: number): number => {
        if (camberMm === 0 || fx <= spanL || fx >= spanR) return 0;
        // Midpoint of Q(P0,C,P1) = (P0+P1)/4 + C/2 => own arch = 4c.t(1-t).
        return 4 * camberMm * bowT(fx) * (1 - bowT(fx));
      };
      // Reverse (Pete 2026-10-25, PHYSICS per Pete + FINAL BOOST): a
      // reverse ski has the SAME tip/tail blocks as a camber ski — the
      // press cures the resin faster on the topside, which bows the ski so
      // the camber REVERSES. The display keeps the drawn curve EXACTLY as
      // constructed (tail arc R400 + bow + nose arc R350) with the bow
      // dipping below the contact chord — one consistent bow, no separate
      // belly, no humps. Boost deepens the dip by the SAME rule as the
      // cambered types: extra dip = (boost-1) x bow (bowSvgMm is negative
      // for reverse, so the lift pushes DOWN). The dip apex (the camber's
      // center, the ski's lowest spot) anchors ON the reference line.
      // FINAL BOOSTS (Pete 2026-10-25): Low 1.8 / Medium 2.5 / High 2.5 /
      // Reverse 2.5 (12.5mm bow, contacts lift ~5.6mm off the line).
      // Anchor: the dip apex must sit ON the dotted reference line. The
      // drawn apex rests camberMm below the contact chord; the dotted line
      // sits floatMm (=0.6pt in mm — moved from 1.5pt, Pete 2026-10-27
      // "just touch") below it — shift the whole display so apex lands
      // exactly on the line (keeps the same blocks, no float).
      const floatMm = 0.6 / profYScaleMm;
      // dip depth at apex = boost x camberMm (bow + extra deepen) — the
      // shift re-anchors the apex on the dotted line at ANY boost.
      const revShift = -floatMm - boost * camberMm;
      // Per-sample display delta (mm):
      const liftMm = (fx: number): number =>
        (boost - 1) * bowSvgMm(fx) + (isReverse ? revShift : 0);
      // facet x per sample: the mapping flips x (tail = RIGHT page edge).
      const boxRight = (CONTENT_X0 + 20) + profBoxW;
      const liftPt = (i: number): number =>
        liftMm((boxRight - mappedProf[i][0]) / profYScaleMm) * profYScaleMm;
      // The ski SITS ON the reference line: the curve is the running surface,
      // so the thickness extends UPWARD from it; the arch lifts the mid.
      const topPts = mappedProf.map(([px, py], i) => [px, py - 2 * half(i) - liftPt(i)] as [number, number]);
      const botPts = mappedProf.map(([px, py], i) => [px, py - liftPt(i)] as [number, number]);
      const outline = [...topPts, ...botPts.reverse()];
      // Pete 2026-10-23: BLUE body + BLACK outline (reference style) — one
      // uniform thin stroke around the whole envelope (equal weight
      // top/bottom), thinner than the previous 1.0/1.2 pair.
      ops.push(...polylineOps(outline, { fill: SKI_FILL, stroke: INK, lineWidth: 0.5 }));
    }
  }

  // S3 reference: horizontal bracket lines below with short vertical
  // end-caps segmenting tip-rocker / camber / tail-rocker; dotted vertical
  // leaders from the transitions; ROCKER / CAMBER / ROCKER centered below.
  const bracketY = ROCKER_BARS_Y + PROF_DESC_SHIFT;
  const bx0 = CONTENT_X0 + 20, bx1 = CONTENT_X1 - 20;
  // Pete 2026-10-26 (Step 3 rocker dial-in): the ROCKER bracket spans
  // "Tip Rocker Run + Nose Length" (resp. "Tail Rocker Run + Tail Length"):
  // the rocker name's FIRST number is the run in cm measured CLOSER TO THE
  // BINDINGS FROM THE END'S WIDEST POINT (profileCurve rockerSpec semantics
  // — '30-10' = upturn starts 300mm inboard of the widest point; the second
  // number is twice the rise in mm at the widest point). The end-to-widest
  // distances are MEASURED from the same sampled outline S2 renders (no
  // guessed constants): on the 230702 production arcs they land at
  // 267.5/138.0mm vs Ullr's Geometry tab 266.77/135.88 (<1mm, bin
  // resolution). Zones for 230702: tip 300+266.77=566.77mm ("about
  // 566.8mm" — Pete), tail 120+135.88=255.88mm.
  let polyMinX = Infinity, polyMaxX = -Infinity;
  for (const [x] of poly) { if (x < polyMinX) polyMinX = x; if (x > polyMaxX) polyMaxX = x; }
  const polySpan = polyMaxX - polyMinX || 1;
  const wProf: Array<{ x: number; w: number }> = [];
  const wBinMm = 5;
  const nWBins = Math.max(2, Math.floor(polySpan / wBinMm));
  for (let i = 0; i < nWBins; i++) {
    const xa = polyMinX + (polySpan * i) / nWBins;
    const xb = polyMinX + (polySpan * (i + 1)) / nWBins;
    let lo = Infinity, hi = -Infinity;
    for (const [x, y] of poly) { if (x >= xa && x < xb) { if (y < lo) lo = y; if (y > hi) hi = y; } }
    if (lo < Infinity) wProf.push({ x: (xa + xb) / 2, w: hi - lo });
  }
  const widest = (zone: Array<{ x: number; w: number }>): { x: number; w: number } | undefined =>
    zone.length ? zone.reduce((a, b) => (b.w > a.w ? b : a)) : undefined;
  // NOSE = LOW-x end of the outline (orientation verified 2026-10-22: the
  // mapped width shoulders read 30.2pt right vs 27.1pt left ≈ 133.3/148.7).
  const noseWidestBin = widest(wProf.filter(b => b.x <= polyMinX + polySpan * 0.3));
  const tailWidestBin = widest(wProf.filter(b => b.x >= polyMinX + polySpan * 0.7));
  const noseWidestFromTipMm = noseWidestBin ? noseWidestBin.x - polyMinX : undefined;
  const tailWidestFromTailMm = tailWidestBin ? polyMaxX - tailWidestBin.x : undefined;
  const rockerRunCm = (label: string): number | null => {
    const m = /^Rocker (\d+)-(\d+)$/.exec(label);
    return m ? Number(m[1]) : null;
  };
  const tipRunCm = rockerRunCm(p.tipRockerLabel);
  const tailRunCm = rockerRunCm(p.tailRockerLabel);
  // Sideview frame: the profile SVG spans 0..facet lengthMm, mapped to
  // bx0..bx1 — the denominator is the ski's real length (sourced), else
  // the outline's own span.
  const lenMm = d.lengthCm.kind === 'sourced' ? d.lengthCm.value * 10 : polySpan;
  // Fallback when the rocker spec is unknown ('Rocker TBD' or unmeasurable
  // outline): the DRAWN CURVE's own contact stations, parsed from the
  // profile SVG's bow path (M <tailContact> .. Q .. <noseContact>) — the
  // geometric minimum zone (where the drawn curve departs the line), never
  // a guess.
  const svgBow = /M ([\d.-]+) [\d.-]+ Q [\d.-]+ [\d.-]+ ([\d.-]+) [\d.-]+/.exec(p.profileSvg);
  const svgTailContactMm = svgBow ? Number(svgBow[1]) : undefined;      // from tail (x=0)
  const svgNoseContactFromTipMm = svgBow ? lenMm - Number(svgBow[2]) : undefined; // len - contactN
  // Pete 2026-10-26 (Step 3 follow-up): "There's another variable ... Nose
  // Inward and Tail Inward. Nose Inward moves the Tip block towards the ski
  // waist (so a negative Nose Inward reduces Tip Rocker run length)."
  // The run is measured from the widest point; sliding the block toward the
  // waist slides the arc's start station the same amount. 230702:
  // noseInwardAdjustment = -150 -> tip zone 566.77-150 = 416.77 ("an actual
  // Tip Rocker length of 417.6mm" — Pete; the reference sheet's drawn tip
  // bracket measures 420mm, confirming the adjusted value was used there).
  // tailInwardAdjustment = 0 -> tail zone stays 255.88.
  // Sign rule: adjustment < 0 = toward the waist = shorter zone.
  const tipZoneAdjMm = p.noseInwardAdjustmentMm ?? 0;
  const tailZoneAdjMm = p.tailInwardAdjustmentMm ?? 0;
  const tipZoneRawMm = tipRunCm !== null && noseWidestFromTipMm !== undefined
    ? tipRunCm * 10 + noseWidestFromTipMm + tipZoneAdjMm
    : undefined;
  const tailZoneRawMm = tailRunCm !== null && tailWidestFromTailMm !== undefined
    ? tailRunCm * 10 + tailWidestFromTailMm + tailZoneAdjMm
    : undefined;
  // ZERO/SHORT-RUN CLAMP (Pete 2026-10-27): same rule as the profile SVG
  // (specSheet.ts sideProfileSvg): at small/zero rocker run the raw zone
  // formula lands INSIDE the block arc (already off the ground — no y=0
  // there), so the camber limit grounds at the block-arc CONTACT instead
  // ("on a ski without rocker the rise starts where the tip/tail block rise
  // begins"). svgBow's ground endpoints carry the contact stations
  // (groundT/groundN = clamped zones), so clamping the bracket zone outward
  // to the bow's own endpoints keeps the bracket ticks and the drawn curve
  // grounded at the SAME stations in every case — including run = 0.
  const tipZoneMm = tipZoneRawMm !== undefined
    ? Math.max(tipZoneRawMm, svgNoseContactFromTipMm ?? 0)
    : svgNoseContactFromTipMm;
  const tailZoneMm = tailZoneRawMm !== undefined
    ? Math.max(tailZoneRawMm, svgTailContactMm ?? 0)
    : svgTailContactMm;
  if (tipZoneMm !== undefined && tailZoneMm !== undefined) {
  // Clamp so the three segments can never cross (long-run rockers).
  const tipFrac = Math.min(0.45, Math.max(0, tipZoneMm / lenMm));
  const tailFrac = Math.min(0.45, Math.max(0, tailZoneMm / lenMm));
  // NAMING (Pete 2026-10-26): the two inner ticks are the TIP ROCKER START
  // and TAIL ROCKER START — "they are the camber ends or limits, they are
  // actually the tip and tail rocker Starts." Same ink, two readings: the
  // CAMBER zone's outer limits, and the stations where each rocker zone
  // begins (walking from the waist outward).
  const xTipRockerStart = bx0 + (bx1 - bx0) * tipFrac;
  const xTailRockerStart = bx1 - (bx1 - bx0) * tailFrac;
  const segs: Array<[number, number]> = [
    [bx0, xTipRockerStart],                  // tip rocker (LEFT, nose)
    [xTipRockerStart, xTailRockerStart],     // camber (middle)
    [xTailRockerStart, bx1],                 // tail rocker (RIGHT)
  ];
  // Bracket lines + end-caps — REF construction (Pete 2026-10-26: "the gap
  // should not exist"): the three bars are CONTIGUOUS, one shared tick per
  // boundary, single tick at each outer end. Measured on the reference at
  // 300dpi: tip bar runs INTO the first camber dash (they overlap a few px);
  // camber's last dash runs into the tail bar; junction has ONE tick column,
  // not two. (The old per-bar ±1.5pt insets doubled every boundary tick and
  // punched a ~3pt hole between bars — what Pete circled.)
  const boundaries = [bx0, xTipRockerStart, xTailRockerStart, bx1];
  // REF style (measured on the reference at 300dpi): ROCKER bars solid;
  // CAMBER bar dashed (73 dash runs, ~4.1px marks at 300dpi -> ~2pt mark,
  // ~2pt gap; dash array [1.2, 2.0] renders close to the ref's rhythm).
  for (let i = 0; i < segs.length; i++) {
    const [xa, xb] = segs[i];
    const isCamber = i === 1;
    ops.push({ op: 'path', d: [
      `${f(xa)} ${f(A4_H - bracketY)} m`, `${f(xb)} ${f(A4_H - bracketY)} l`, 'S',
    ], stroke: GRAY, lineWidth: 0.7, ...(isCamber ? { dash: [1.2, 2.0] } : {}) });
  }
  // One tick per boundary (shared by both bars meeting there).
  for (const xb2 of boundaries) {
    ops.push({ op: 'path', d: [
      `${f(xb2)} ${f(A4_H - (bracketY - 3))} m`, `${f(xb2)} ${f(A4_H - (bracketY + 3))} l`, 'S',
    ], stroke: GRAY, lineWidth: 0.7 });
  }
  // Dotted vertical leaders at the transitions + outer ends.
  for (const xl of [bx0, xTipRockerStart, xTailRockerStart, bx1]) {
    ops.push({ op: 'path', d: [
      `${f(xl)} ${f(A4_H - (bracketY - 12))} m`, `${f(xl)} ${f(A4_H - (bracketY + 3))} l`, 'S',
    ], stroke: [170 / 255, 170 / 255, 170 / 255], lineWidth: 0.5, dash: [1, 1.6] });
  }
  // Labels centered under each bracket (ref: ROCKER / CAMBER / ROCKER).
  const bracketLabels = ['ROCKER', 'CAMBER', 'ROCKER'];
  for (let i = 0; i < segs.length; i++) {
    ops.push(textOp({ x: (segs[i][0] + segs[i][1]) / 2, y: bracketY + 8, size: 8, text: bracketLabels[i], color: GRAY, align: 'center' }));
  }
  } // end brackets (rocker-spec known)

  // ---- STEP 4 (S4): MATERIALS (Pete kanban 2026-10-25: section head
  // CONSTRUCTION->MATERIALS; sub-head CORE->CONSTRUCTION; the laminate art
  // is placed at the reference's measured box, labels rendered as sheet
  // text LEFT of the art — the asset no longer carries baked-in labels) ----
  const c = data.construction;
  ops.push(textOp({ x: CONTENT_X0, y: CON_HEAD_Y, size: 15, text: 'YOUR BUILD', weight: 700, color: HEADINGS }));
  // Three label+description blocks, REF layout (measured 2026-10-25): bold caps
  // label INLINE with its description on the same line (e.g. 'CORE: Paulownia
  // wood core'), continuation lines hang below at the label's x. Body block is
  // INDENTED to x139 (head sits at x120); descriptions wrap at x286.
  const S4_BODY_X = 139;                 // ref: labels/descs indent to x~139
  const S4_WRAP_X = 286;                 // ref: description wrap boundary (x286.3)
  const labelColW = S4_WRAP_X - S4_BODY_X;
  let hy = CON_HEAD_Y + 14;   // ref: first sub-block ~15pt under the head
  const labelBlocks: Array<[string, string]> = [
    ['CONSTRUCTION:', c.headline],
    ['FLEX PATTERN:', `${c.flexPattern}. Tail slightly stiffer than tip.`],
    ['STIFFNESS:', `${c.stiffness}. Calibrated for your height, weight, and skier preferences.`],
    // Pete 2026-10-27: BINDINGS line from the Sales-tab Binding Type dropdown;
    // OMITTED entirely when the binding is NONE or OTHER. (SKI PACKAGE line
    // removed — 'not helpful, the page must be concise'.)
  ];
  // Pete 2026-10-30: package upgrades block (Silver / Ultra / Flow bundles) —
  // header line + the bundle's material upgrades, UNDER STIFFNESS and BEFORE
  // BINDINGS. No bundle (Basic/Other packages) = no lines at all.
  if (c.packageUpgrades) {
    // Pete 2026-10-30: NO per-upgrade labels — the sentences ride inline
    // after the header ('SILVER PACKAGE UPGRADES: World Cup Base for
    // improved glide efficiency. ...').
    labelBlocks.push([`${c.packageUpgrades.header}:`, c.packageUpgrades.lines.join(' ')]);
  }
  // Pete 2026-10-27: just the binding type — no commentary for now.
  if (c.bindings) labelBlocks.push(['BINDINGS:', c.bindings]);
  // Pete 2026-10-28: the GRAPHICS section folds into YOUR BUILD — the Top
  // Graphic description is just another line here, same size/look as BINDINGS.
  if (c.graphic) labelBlocks.push(['TOPSHEET GRAPHIC:', c.graphic]);
  for (const [label, desc] of labelBlocks) {
    const labelW = textWidthBarlow(label, 9.5, 700) + 6;  // + gap after colon
    // First line shares the row with the label -> narrower; continuation
    // lines start at the body x and get the FULL column width (ref behavior).
    // Greedy-fill line 0 within the label clearance, then RE-WRAP the entire
    // remainder at the full column width (never prepend without re-wrapping —
    // that pushed line 1 past x286 into the layer-label column).
    const words = desc.split(' ');
    const line0: string[] = [];
    const rest = words;
    while (rest.length) {
      const candidate = line0.length ? [...line0, rest[0]] : [rest[0]];
      if (textWidthBarlow(candidate.join(' '), 9, 400) <= labelColW - labelW || !line0.length) {
        line0.push(rest.shift() as string);
      } else break;
    }
    const descLines = [line0.join(' '), ...wrapText(rest.join(' '), 9, 400, labelColW)].filter(l => l.length > 0);
    ops.push(textOp({ x: S4_BODY_X, y: hy, size: 9.5, text: label, weight: 700, color: HEADINGS }));
    ops.push(textOp({ x: S4_BODY_X + labelW, y: hy + 0.4, size: 9, text: descLines[0] ?? '', color: GRAY }));
    hy += 11;
    for (let di = 1; di < descLines.length; di++) {
      ops.push(textOp({ x: S4_BODY_X, y: hy, size: 9, text: descLines[di], color: GRAY }));
      hy += 11;
    }
    hy += 4;   // ref: FLEX PATTERN follows 15pt after the last desc line
  }

  // Laminate stack art — the reference box, UNDISTORTED (asset is 734x839,
  // aspect 0.875; box w=138.2pt -> h=157.9pt, clipped to the band).
  const lamW = LAM_X1 - LAM_X0;
  const lamH = Math.min(lamW / (734 / 839), LAM_Y1 - LAM_Y0);
  ops.push({ op: 'image', x: LAM_X0, y: A4_H - (LAM_Y0 + lamH), w: lamW, h: lamH, jpegBase64: LAMINATE_STACK_JPEG_B64 });
  // Per-layer labels LEFT of the illustration (ref: right-aligned to x392.6,
  // ~21.1pt pitch, 8.5pt ALL-CAPS #666565) — data-driven from materialsStack.
  const layers = c.materialsStack ?? [];
  const lamLabelCount = layers.length;
  let ly = LAM_Y0 + 10;
  const lStep = lamLabelCount > 1 ? (LAM_Y1 - LAM_Y0 - 20) / (lamLabelCount - 1) : 0;
  void lStep;
  for (const layer of layers) {
    ops.push(textOp({ x: LAM_LABEL_X, y: ly, size: 8.5, text: layer.toUpperCase(), color: GRAY, align: 'right' }));
    ly += lStep;
  }

  // ---- MATERIAL LAYUP (Pete 2026-11-03): the ski's layer stack, TOP
  // material first, working down — derived from the order form
  // (materialLayupFor in orderProposalSheet), never hand-copied. Rendered as
  // a numbered inline list wrapped at the full content width. The head
  // anchors BELOW the YOUR BUILD text block AND the laminate art box (the
  // art occupies x413..552 down to LAM_Y1, so a full-width section must
  // clear it) — never a fixed constant.
  const layup = data.construction.materialLayup ?? [];
  let layEndY = hy;
  if (layup.length) {
    const layHeadY = Math.max(LAM_Y1 + 14, hy + 18);
    ops.push(textOp({ x: CONTENT_X0, y: layHeadY, size: 15, text: 'MATERIAL LAYUP', weight: 700, color: HEADINGS }));
    const numbered = `Top to bottom: ${layup.map((l, i) => `${i + 1}. ${l}`).join('  /  ')}`;
    let layY = layHeadY + 16;
    for (const ln of wrapText(numbered, 9, 400, CONTENT_X1 - CONTENT_X0)) {
      ops.push(textOp({ x: CONTENT_X0, y: layY, size: 9, text: ln, color: GRAY }));
      layY += 11;
    }
    layEndY = layY - 11;   // baseline of the last wrapped line
  }

  // ---- STEP 5b: DESIGN SUMMARY (Pete 2026-10-28) — the Sales
  // Recommendation paragraph from the bottom of the Ski Proposal, as its own
  // section below TOPSHEET GRAPHIC. Head identical to DIMENSIONS / PROFILE /
  // YOUR BUILD (15pt Barlow bold, palette headings); body 9pt gray, wrapped
  // across the full content width. Hidden when the paragraph is absent.
  if (data.designSummary) {
    // Pete 2026-10-28: section title is HOW THEY'LL RIDE (was DESIGN SUMMARY —
    // "the section describes how the skis will feel and perform").
    // YOUR BUILD is a DYNAMIC block (CONSTRUCTION/FLEX/STIFFNESS/BINDINGS/
    // TOPSHEET GRAPHIC wrap variably) — the head anchors BELOW the block's
    // last line (hy + clearance), never below a fixed constant, or long
    // graphic descriptions collide with it (caught live on order 27285).
    // Page-fit clamp (Pete 2026-11-01: "Is the pricing section getting cut
    // off? Make sure everything fits"): on long ride paragraphs the DS+PRICING
    // tail ran past the page bottom (live on 28754: URL bbox bottom 845.8 vs
    // page 841.9). The preferred generous gaps (Pete 2026-10-28) tighten only
    // as far as needed to end the URL line above a 20pt bottom margin -
    // floors: prGap 12, dsGap 8. Measured constants (28754 calibration): the
    // URL line's bbox bottom sits ~1.1pt below its baseline, and the PRICING
    // block adds 16 (head->lead-in) + 12 (lead-in->URL) baseline steps.
    const dsLines = wrapText(data.designSummary, 9, 400, CONTENT_X1 - CONTENT_X0);
    const PAGE_BOTTOM_LIMIT = A4_H - 20;
    const URL_BBOX_BOTTOM_OFF = 1.1;
    // Graduated page-fit clamp (Pete 2026-11-02 "Pricing section is getting cut
    // off" — live on 27285: the single-pass floors prGap 12 / dsGap 8 could NOT
    // recover enough because the ULTRA PACKAGE UPGRADES block added 5 wrapped
    // lines above, leaving the URL bottom at 827.6 vs the 821.9 limit). The
    // levers now tighten IN ORDER until the URL fits, each only as far as
    // needed: (1) prGap 26->12, (2) dsGap 20->8, (3) prGap 12->6, (4) dsGap
    // 8->3, (5) PRICING head->lead 16->10 + lead->URL 12->8, (6) ride line
    // pitch 11->10. The generous look survives on short orders; page fit is a
    // hard guarantee on long ones.
    let dsGap = 20;
    let prGap = 26;
    let prHeadToLead = 16;
    let leadToUrl = 12;
    let dsPitch = 11;
    // 2026-11-03: the MATERIAL LAYUP section (when present) sits between
    // YOUR BUILD and HOW THEY'LL RIDE — the ride head and the URL projection
    // stack on ITS bottom, not on hy, or the tail collides with the layup
    // lines on long orders.
    const dsPrevEnd = layup.length ? layEndY : hy;
    const hasUrl = !!data.pricing.invoiceUrl;
    // With a URL the tail is the URL line's bbox bottom; without one it is
    // the last ride line's own bbox bottom (the no-URL tail was previously
    // unclamped — the MATERIAL LAYUP section pushed it past the margin).
    const projectedUrlY = (aDsgap: number, aPrgap: number, aH2l: number, aL2u: number, aPitch: number) => {
      const dsHeadProbe = Math.max(DS_HEAD_Y, dsPrevEnd + aDsgap);
      if (!hasUrl) return dsHeadProbe + 16 + aPitch * (dsLines.length - 1) + URL_BBOX_BOTTOM_OFF;
      return dsHeadProbe + 16 + aPitch * dsLines.length + aPrgap + aH2l + aL2u;
    };
    {
      // graduated stages: [prGap floor, dsGap floor, prHeadToLead floor, leadToUrl floor, dsPitch]
      // No-URL stages hold prGap at its generous value (it plays no role in
      // the ride-bottom projection, so cutting it would only eat budget).
      const stages: Array<[number, number, number, number, number]> = hasUrl ? [
        [12, 20, 16, 12, 11],
        [12, 8, 16, 12, 11],
        [6, 8, 16, 12, 11],
        [6, 3, 16, 12, 11],
        [6, 3, 10, 8, 11],
        [6, 3, 10, 8, 10],
        // 2026-11-03: the MATERIAL LAYUP section adds ~44pt the old floors
        // could not recover on long orders — two deeper stages.
        [4, 2, 10, 8, 10],
        [4, 2, 8, 6, 9],
      ] : [
        [26, 12, 16, 12, 11],
        [26, 8, 16, 12, 11],
        [26, 8, 16, 12, 10],
      ];
      for (const [prFloor, dsFloor, h2lFloor, l2uFloor, pitch] of stages) {
        // tighten each lever toward this stage's floor, in order, only as far as needed
        const over0 = projectedUrlY(dsGap, prGap, prHeadToLead, leadToUrl, dsPitch) + URL_BBOX_BOTTOM_OFF - PAGE_BOTTOM_LIMIT;
        if (over0 <= 0) break;
        const over = over0;
        const prCut = Math.min(prGap - prFloor, over);
        prGap -= prCut;
        const dsCut = Math.min(dsGap - dsFloor, over - prCut);
        dsGap -= dsCut;
        const h2lCut = Math.min(prHeadToLead - h2lFloor, over - prCut - dsCut);
        prHeadToLead -= h2lCut;
        const l2uCut = Math.min(leadToUrl - l2uFloor, over - prCut - dsCut - h2lCut);
        leadToUrl -= l2uCut;
        if (over - prCut - dsCut - h2lCut - l2uCut > 0) dsPitch = pitch;
        // 2026-11-03 FIX (latent bug, fatal once MATERIAL LAYUP added ~44pt):
        // the old break fired when the CURRENT stage's floors were met even
        // with overflow remaining - so the ladder always stopped at stage 1
        // and the deeper stages never ran. Break only when the residual
        // overflow is resolved; deeper stages do the rest.
        if (over - prCut - dsCut - h2lCut - l2uCut <= 0) break;
      }
    }
    // Browser-safe env probe (no node types in this bundle).
    const _dbg = (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.DEBUG_LAYUP;
    if (_dbg) console.log('[layup-fit]', JSON.stringify({ dsPrevEnd, hy, layEndY, dsLines: dsLines.length, dsGap, prGap, prHeadToLead, leadToUrl, dsPitch, hasUrl }));
    const dsHeadY = Math.max(DS_HEAD_Y, dsPrevEnd + dsGap);
    ops.push(textOp({ x: CONTENT_X0, y: dsHeadY, size: 15, text: "HOW THEY'LL RIDE", weight: 700, color: HEADINGS }));
    let dsY = dsHeadY + 16;
    for (const ln of dsLines) {
      ops.push(textOp({ x: CONTENT_X0, y: dsY, size: 9, text: ln, color: GRAY }));
      dsY += dsPitch;
    }

    // ---- PRICING (Pete 2026-10-28): link to the itemized Shopify draft
    // invoice below the ride paragraph. Hidden when the order carries no
    // draft invoice URL (nothing to link to — never a dead 'here:' line).
    if (data.pricing.invoiceUrl) {
      const url = data.pricing.invoiceUrl;
      // Pete 2026-10-28 follow-up: MORE white space between the ride paragraph
      // and PRICING — head baseline ~26pt under the last ride line (was 8);
      // tightened by the page-fit clamp above only when the tail would not fit.
      const prHeadY = dsY + prGap;
      ops.push(textOp({ x: CONTENT_X0, y: prHeadY, size: 15, text: 'PRICING', weight: 700, color: HEADINGS }));
      const prLeadY = prHeadY + prHeadToLead;
      ops.push(textOp({ x: CONTENT_X0, y: prLeadY, size: 9,
        // Pete 2026-10-30: option-1 messaging — 'invoice' is internal Shopify
        // vocabulary and reads like a bill owed; customer-facing copy says
        // what the click does (must match the email CTA exactly).
        text: 'Your custom build, itemized - review the price and order your skis:', color: GRAY }));
      const prUrlY = prLeadY + leadToUrl;
      ops.push(textOp({ x: CONTENT_X0, y: prUrlY, size: 9, text: url, color: [0 / 255, 86 / 255, 125 / 255] }));
      // Clickable link annotation over the URL text (rect in PDF space:
      // pdfCore link ops are raw /Rect coords, y bottom-up).
      const urlW = textWidthBarlow(url, 9, 400);
      ops.push({ op: 'link', x: CONTENT_X0, y: A4_H - prUrlY - 2.5, w: urlW, h: 10.5, url });
    }
  }

  // ---- footer ---- REMOVED (Pete 2026-10-28, attachment: 'delete the
  // bottom footer line'). The rule went first (2026-10-27); the text line is
  // gone now too. footerLine/designVersion remain in the data contract for
  // other consumers (proposal assembly) — just not rendered on the sheet.

  return buildPdfWithFonts([{ ops }], 'wagner');
}

// ---------------------------------------------------------------------------
// Arc geometry: sample in SVG space with the serializers' OWN semantics, then
// map affinely into the page box. No sweep-flag reinterpretation.
// ---------------------------------------------------------------------------

/**
 * Sample the plan-view perimeter into a polyline (mm, y-down SVG space),
 * using exactly the P2-verified arc semantics (arcToSvgPath's angle math).
 * Adaptive density: chord spacing <= ~1mm on every arc (giant-radius sidecut
 * arcs span >1300mm — fixed 24-sample density left 27mm gaps there, which
 * the shape-fidelity test caught 2026-10-15).
 */
export function samplePlanViewPolyline(arcs: RenderArc[], minPerArc = 24): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (const a of arcs) {
    let s0: number, e0: number, cx: number;
    if (a.d === 1) {
      cx = a.x;
      s0 = a.s;
      e0 = a.e < a.s ? a.e + 360 : a.e;
    } else {
      // VERIFIED d=-1 semantics (renderFacet v3, 0.000mm on prod arcs):
      // center = (-x, y); start angle = 180-s; end angle = 180-e. The end
      // angle may be LESS than start (clockwise sweep) — interpolate
      // directly, NEVER wrap (+360 sends giant-radius sidecut arcs the long
      // way around the circle; caught by the shape-fidelity test 2026-10-15).
      cx = -a.x;
      s0 = 180 - a.s;
      e0 = 180 - a.e;
    }
    const spanRad = (Math.abs(e0 - s0) * Math.PI) / 180;
    const arcLenMm = spanRad * a.r;
    const n = Math.max(minPerArc, Math.min(512, Math.ceil(arcLenMm)));
    for (let i = 0; i <= n; i++) {
      const ang = s0 + ((e0 - s0) * i) / n;
      pts.push(pointOnArc(cx, a.y, a.r, ang));
    }
  }
  return pts;
}

/** Map a sampled mm polyline (y-down) into the page plan-view box (top-down).
 * flipX=true mirrors the length axis so the NOSE lands on the LEFT (S2/S3
 * reference orientation: tip left, tail right). */
function mapPolyline(
  pts: Array<[number, number]>,
  x0: number, y0: number, w: number, h: number,
  flipX = false,
): Array<[number, number]> {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x, y] of pts) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const sx = w / (maxX - minX), sy = h / (maxY - minY);
  const s = Math.min(sx, sy);
  const ox = x0 + (w - (maxX - minX) * s) / 2 - minX * s;
  const oy = y0 + (h - (maxY - minY) * s) / 2 - minY * s;
  // flipX mirrors about the BOX CENTER: x' = 2*(x0 + w/2) - mapped_x. (The
  // earlier x0 + w - mapped_x form shifted the whole shape left by x0 —
  // the sideview landed at x0..395 instead of 140..535.)
  return pts.map(([x, y]) => [flipX ? 2 * (x0 + w / 2) - (ox + x * s) : ox + x * s, oy + y * s] as [number, number]);
}

/** Map several polylines with ONE shared affine (union bounds of all points)
 *  so sub-paths keep their relative positions. flipX mirrors about the box center. */
function mapPolylineShared(
  paths: Array<Array<[number, number]>>,
  all: Array<[number, number]>,
  x0: number, y0: number, w: number, h: number,
  flipX = false,
  /** Uniform mm->pt scale overriding the fit-to-box y affine. When set, y
   *  uses `yScaleMm` pt/mm and the mapping anchors `anchorYSvg` (default: the
   *  LARGEST-y point of `all` — the baseline) at the band's bottom (y0 + h) —
   *  the profile sideview's true-scale mode (Pete 2026-10-22: true scale
   *  ALWAYS). Explicit anchor matters for Reverse: the camber bow DIPS below
   *  the baseline, and pinning the dip apex instead of the baseline would
   *  float the whole ski up by the dip height (Pete 2026-10-24). */
  yScaleMm?: number,
  anchorYSvg?: number,
): Array<Array<[number, number]>> {
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const [x, y] of all) {
    if (x < minX) minX = x; if (x > maxX) maxX = x;
    if (y < minY) minY = y; if (y > maxY) maxY = y;
  }
  const sx = w / (maxX - minX);
  const sy = yScaleMm !== undefined ? yScaleMm : h / (maxY - minY);
  const ox = x0 - minX * sx;
  // anchor: without a uniform scale, fit the span into [y0, y0+h]; with one,
  // plant the anchor y (default the baseline = maxY) at the band bottom.
  const anchorY = anchorYSvg ?? maxY;
  const oy = yScaleMm !== undefined ? (y0 + h) - anchorY * sy : y0 - minY * sy;
  const map1 = (x: number): number => flipX ? 2 * (x0 + w / 2) - (ox + x * sx) : ox + x * sx;
  return paths.map(pp => pp.map(([x, y]) => [map1(x), oy + y * sy] as [number, number]));
}

function polylineOps(
  pts: Array<[number, number]>,
  style: { fill?: [number, number, number]; stroke?: [number, number, number]; lineWidth?: number; dash?: [number, number] },
): PdfOp[] {
  const ops: PdfOp[] = [];
  if (pts.length < 2) return ops;
  const d: string[] = [`${f(pts[0][0])} ${f(A4_H - pts[0][1])} m`];
  for (let i = 1; i < pts.length; i++) {
    d.push(`${f(pts[i][0])} ${f(A4_H - pts[i][1])} l`);
  }
  d.push(style.fill && style.stroke ? 'B' : style.fill ? 'f' : 'S');
  ops.push({ op: 'path', d, ...style });
  return ops;
}

/**
 * Sample the contract's profile SVG into a polyline in SVG space (y-down),
 * arcs included, using the same sample-first-map-second discipline.
 */
/** Sample EVERY <path> in the profile SVG, keeping them SEPARATE so the
 *  renderer can distinguish the ski CURVE (bow + tip/tail arcs) from the
 *  dashed BASELINE. The old first-path-only bug rendered the sideview from
 *  the BASELINE alone (a flat line — no upturns, no camber). */
/** The SVG's ground-line y in svg space — the FIRST path is the baseline
 *  (`M 0 <y> L <L> <y>`); the bow may dip BELOW it (Reverse), so the viewBox
 *  height (which includes dip headroom) is NOT the anchor. */
export function sideProfileSvgHeight(svg: string): number {
  const m = /d="M 0(?:\.0)? ([\d.]+) L/.exec(svg);
  return m ? Number(m[1]) : 0;
}

export function sampleProfilePaths(svg: string, perArc = 24): Array<Array<[number, number]>> {
  const pathMatches = [...svg.matchAll(/ d="([^"]+)"/g)];
  const out: Array<Array<[number, number]>> = [];
  for (const pathMatch of pathMatches) {
    const sub = sampleProfilePath(pathMatch[1], perArc);
    if (sub.length > 1) out.push(sub);
  }
  return out;
}

/** Union polyline of all profile paths (compat shim). */
export function sampleProfileSvg(svg: string, perArc = 24): Array<[number, number]> {
  const all: Array<[number, number]> = [];
  for (const sub of sampleProfilePaths(svg, perArc)) {
    if (all.length && Math.abs(all[all.length - 1][0] - sub[0][0]) < 1e-6 && Math.abs(all[all.length - 1][1] - sub[0][1]) < 1e-6) {
      all.push(...sub.slice(1));
    } else {
      all.push(...sub);
    }
  }
  return all;
}

function sampleProfilePath(d: string, perArc = 24): Array<[number, number]> {
  const tokens = d.trim().split(/\s+/);
  const pts: Array<[number, number]> = [];
  let last: [number, number] | null = null;
  let i = 0;
  while (i < tokens.length) {
    const t = tokens[i];
    if (t === 'M' || t === 'L') {
      last = [Number(tokens[i + 1]), Number(tokens[i + 2])];
      pts.push(last);
      i += 3;
    } else if (t === 'Q') {
      // quadratic in SVG space, sampled. N=64: the bow spans ~1470mm; at
      // N=16 the 92mm chord spacing undershoots the parabola's steep ends
      // by ~0.4mm (widest-point anchors read 3.6mm instead of 4, probe
      // 2026-10-24). 64 pts -> ~23mm spacing, sub-0.03mm chord error.
      const q: [number, number] = [Number(tokens[i + 1]), Number(tokens[i + 2])];
      const e: [number, number] = [Number(tokens[i + 3]), Number(tokens[i + 4])];
      const p0 = last ?? q;
      const N = 64;
      for (let k = 1; k <= N; k++) {
        const u = k / N;
        const x = (1 - u) * (1 - u) * p0[0] + 2 * (1 - u) * u * q[0] + u * u * e[0];
        const y = (1 - u) * (1 - u) * p0[1] + 2 * (1 - u) * u * q[1] + u * u * e[1];
        pts.push([x, y]);
      }
      last = e;
      i += 5;
    } else if (t === 'A') {
      const rx = Number(tokens[i + 1]);
      const large = Number(tokens[i + 4]);
      const sweep = Number(tokens[i + 5]);
      const e: [number, number] = [Number(tokens[i + 6]), Number(tokens[i + 7])];
      const s = last ?? e;
      pts.push(...sampleSvgArcSvgSpace(s, e, rx, large, sweep, perArc));
      last = e;
      i += 8;
    } else i += 1;
  }
  return pts;
}

/** SVG-space arc sampling (y-DOWN) — sweep=1 is clockwise here, as the SVG
 *  spec defines it. This is the function the first version got wrong by
 *  trying to be clever about the y-flip; now the flip happens only in the
 *  final map step, exactly once. */
export function sampleSvgArcSvgSpace(
  s: [number, number], e: [number, number], r: number,
  largeArc: number, sweep: number, N = 24,
): Array<[number, number]> {
  const dx = e[0] - s[0], dy = e[1] - s[1];
  const dist = Math.hypot(dx, dy);
  if (dist < 1e-9) return [];
  const rc = Math.max(Math.abs(r), dist / 2);
  const h = Math.sqrt(Math.max(rc * rc - (dist / 2) * (dist / 2), 0));
  const mx = (s[0] + e[0]) / 2, my = (s[1] + e[1]) / 2;
  const ux = -dy / dist, uy = dx / dist;
  const c1: [number, number] = [mx + ux * h, my + uy * h];
  const c2: [number, number] = [mx - ux * h, my - uy * h];
  // SVG y-down: sweep=1 => clockwise (negative math-angle direction).
  // span measured CW from s to e about a candidate:
  const cwSpan = (c: [number, number]): number => {
    const a0 = Math.atan2(s[1] - c[1], s[0] - c[0]);
    const a1 = Math.atan2(e[1] - c[1], e[0] - c[0]);
    let dlt = a0 - a1; // cw positive
    while (dlt <= 0) dlt += Math.PI * 2;
    return dlt;
  };
  const wantCw = sweep === 1;
  const fits = (c: [number, number]): boolean => {
    const sp = cwSpan(c);
    if (wantCw) return largeArc === 1 ? sp > Math.PI - 1e-9 : sp <= Math.PI + 1e-9;
    const ccw = 2 * Math.PI - sp;
    return largeArc === 1 ? ccw > Math.PI - 1e-9 : ccw <= Math.PI + 1e-9;
  };
  const center = fits(c1) ? c1 : c2;
  const a0 = Math.atan2(s[1] - center[1], s[0] - center[0]);
  let delta = Math.atan2(e[1] - center[1], e[0] - center[0]) - a0;
  if (wantCw) { while (delta >= -1e-9) delta -= Math.PI * 2; if (largeArc === 0 && -delta > Math.PI) delta += Math.PI * 2; }
  else { while (delta <= 1e-9) delta += Math.PI * 2; if (largeArc === 0 && delta > Math.PI) delta -= Math.PI * 2; }
  const out: Array<[number, number]> = [];
  for (let k = 1; k <= N; k++) {
    const a = a0 + (delta * k) / N;
    out.push([center[0] + rc * Math.cos(a), center[1] + rc * Math.sin(a)]);
  }
  return out;
}
